import dayjs from 'dayjs'
import { groupBy, uniqBy } from 'lodash'
import { useEffect, useMemo } from 'react'

import { AlmanacFileCalendarEvent, CalendarFilters } from 'screens/Calendar/types'
import { ASPECT_RATIOS, DesignChannel, getAspectRatioKeyFromDesignChannel } from 'components/NewFileModal/Types'

import type { BrandPlanScheduleContent, BrandPlanScheduleContentStatus } from 'types'
import { SuggestionRequestStatus } from 'types'
import { getCrosspostTargets } from 'screens/Calendar/FullScreenPreview/utils'
import { usePendingBrandPlanSchedules } from 'hooks/queries/schedules/usePendingBrandPlanSchedules'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { useCalendarRealtime } from 'hooks/queries/calendar/useCalendarRealtime'
import { useCalendar } from 'screens/Calendar/useCalendar'
import { useArticlePreviewSync } from 'screens/Calendar/useArticlePreviewSync'
import { useDesignPreviewSync } from 'screens/Calendar/useDesignPreviewSync'
import { AlmanacFileIndexItem } from 'hooks/queries/files/types'
import { useSyncFilePreviewImage } from 'screens/Calendar/WeekViewV2/useSyncFilePreviewImage'
import { parsePostDate } from 'utils/time'
import { useRootStore } from 'stores/RootStore'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

type Input = {
  channels?: string[]
  currentMonth: { year: number; month: number } | null
  externalIntegrationIds?: number[]
  projectKey?: string
  projectKeys?: string[]
  statuses?: PostStatusPropertyValue[]
}

type Output = {
  allEvents: AlmanacFileCalendarEvent[]
  allGeneratingContentsHaveLoadedPreview: boolean
  eventsByDay: Record<string, AlmanacFileCalendarEvent[]>
  eventsData: AlmanacFileCalendarEvent[]
  filters: CalendarFilters
  hasInitialData: boolean
  isGenerating: boolean
  isLoading: boolean
  tempPosts: BrandPlanScheduleContent[]
}

export const useEvents = (input: Input): Output => {
  const { projectKeys, projectKey, externalIntegrationIds, statuses, channels, currentMonth } = input

  const calendarV2 = useFlipperFlag('calendar_v2')
  const { contentGeneration: generationStore } = useRootStore()

  const { syncFilePreviewImage } = useSyncFilePreviewImage()

  const tempPosts = generationStore.generatingContents
  const isGenerating = generationStore.isActive
  const allGeneratingContentsHaveLoadedPreview = generationStore.isGenerationDone

  const { data: schedules } = usePendingBrandPlanSchedules()
  const pendingSchedule = useMemo(
    () => schedules?.find((schedule) => schedule.status === 'processing' || schedule.status === 'pending'),
    [schedules],
  )

  const filters = { projectKeys: projectKeys || [projectKey || ''], externalIntegrationIds, statuses, channels }
  useCalendarRealtime(filters)
  const { eventsData, hasInitialData, isLoading } = useCalendar({
    filters,
    currentMonth,
    options: { include_scheduled_post: true, include_design_content: true, include_brand_plan_schedule_content: true },
  })
  useArticlePreviewSync(calendarV2 ? [] : eventsData)
  useDesignPreviewSync(calendarV2 ? [] : eventsData)

  const tempPostsEvents = useMemo(() => {
    if (calendarV2) return [] as AlmanacFileCalendarEvent[]
    const hiddenStatuses: BrandPlanScheduleContentStatus[] = [
      SuggestionRequestStatus.Expired,
      SuggestionRequestStatus.Aborted,
    ]
    return tempPosts
      .filter((post) => !hiddenStatuses.includes(post.status))
      .map((post) => {
        const aspectRatio = ASPECT_RATIOS[getAspectRatioKeyFromDesignChannel(post.type as DesignChannel, 1, 2)]
        const channel = post.almanac_file?.channel || post.type

        // Get the appropriate crosspost targets based on channel type
        // The function will automatically use VIDEO_CROSSPOST_TACTICS for video channels
        // and CROSSPOST_TACTICS for regular social channels
        const crosspostTargets = post.is_crosspost ? getCrosspostTargets(channel, pendingSchedule?.tactics) : []

        const almanacFile = post.almanac_file
        const mappedFile = almanacFile
          ? {
              id: almanacFile.key,
              key: almanacFile.key,
              title: almanacFile.title || null,
              post_at: almanacFile.post_at || null,
              created_at: almanacFile.created_at || null,
              updated_at: almanacFile.updated_at || null,
              viewed_at: almanacFile.viewed_at || null,
              post_status: (almanacFile.status as PostStatusPropertyValue) || null,
              asset_type: almanacFile.asset_type || null,
              channel: almanacFile.channel || null,
              image_url: almanacFile.image_url || null,
              duration: almanacFile.duration || null,
              post_url: almanacFile.post_url || null,
              image_url_generating: almanacFile.image_url_generating || false,
              scheduled_post_targets: almanacFile.scheduled_post_targets || crosspostTargets,
              emoji: almanacFile.emoji || null,
              channel_emoji: almanacFile.channel_emoji || null,
              parent_id: almanacFile.parent_id || null,
              external_integration_id: almanacFile.external_integration_id || null,
              design_content: almanacFile.design_content || null,
              content: almanacFile.content || null,
              width: almanacFile.width || null,
              height: almanacFile.height || null,
              did_generation_fail: almanacFile.did_generation_fail || false,
              is_generating: almanacFile.is_generating || false,
              is_design_blank: almanacFile.is_design_blank || false,
              progress: almanacFile.progress || null,
              content_type: almanacFile.content_type || null,
            }
          : null

        return {
          ...post,
          key: mappedFile?.key,
          id: mappedFile?.key,
          title: '',
          asset_type: mappedFile?.asset_type,
          post_at: parsePostDate(post.post_at),
          start: parsePostDate(post.post_at),
          end: parsePostDate(post.post_at),
          image_url: mappedFile?.image_url,
          channel,
          content: mappedFile?.content,
          postable: false,
          width: mappedFile?.width || aspectRatio?.width,
          height: mappedFile?.height || aspectRatio?.height,
          is_generating: mappedFile?.is_generating || false,
          image_url_generating: true,
          scheduleContentId: post.id.toString(),
          design_content: mappedFile?.design_content,
          requestId: post.id,
          scheduled_post_targets: crosspostTargets,
          scheduled_post: undefined,
          did_generation_fail: mappedFile?.did_generation_fail || false,
          is_design_blank: mappedFile?.is_design_blank || false,
          progress: post.request?.progress || mappedFile?.progress || null,
          content_type: mappedFile?.content_type || post.content_type || null,
          reference_image_url:
            post.request?.prompt?.reference_image_url || post.request?.prompt?.reference_urls?.[0] || null,
        }
      })
  }, [calendarV2, tempPosts, pendingSchedule])

  // Array of all the events that are in the calendar plus temporary events for posts that are being generated.
  // For the temporary events, we want to keep the ones that comply with the following criteria:
  // - are pending, or successful and in the tempPostsRequestIds array
  // - are not in the eventsData array
  //
  // Sort posts by post_at and then by channel name
  const events = useMemo(() => {
    // Build a set of scheduleContentIds present in real events for fast lookup
    const realScheduleContentIds = new Set(eventsData.map((e) => e.scheduleContentId).filter(Boolean))

    return uniqBy(
      [
        ...eventsData.map((event) => {
          const tempMatch = tempPostsEvents.find(
            (temp) =>
              temp.key === event.key || (temp.scheduleContentId && temp.scheduleContentId === event.scheduleContentId),
          )
          const tempEventTargets = tempMatch?.scheduled_post_targets
          const mergedTargets =
            event.scheduled_post_targets?.length || tempEventTargets?.length
              ? event.scheduled_post_targets || tempEventTargets
              : []

          // Poll data (temp events) is more up-to-date than the calendar cache for
          // generation-related fields. Prefer temp data for image/content/progress
          // so the card reflects the latest state without waiting for a cache refresh.
          const mergedImage = tempMatch?.image_url || event.image_url
          const mergedDesignContent = tempMatch?.design_content || event.design_content
          const mergedContent = tempMatch?.content || event.content
          const mergedContentType = tempMatch?.content_type || event.content_type

          return {
            ...event,
            scheduleContentId: tempMatch?.scheduleContentId || event.scheduleContentId || null,
            scheduled_post_targets: mergedTargets || event.scheduled_post_targets || [],
            scheduled_post: undefined,
            ...(tempMatch
              ? {
                  image_url: mergedImage,
                  design_content: mergedDesignContent,
                  content: mergedContent,
                  content_type: mergedContentType,
                  progress: tempMatch.progress,
                  is_generating: tempMatch.is_generating,
                  image_url_generating: tempMatch.image_url_generating,
                }
              : {}),
          }
        }),
        // Only include temp events that have no matching real event.
        // Match by file key OR by scheduleContentId to prevent duplicates.
        ...tempPostsEvents.filter(
          (post) =>
            !eventsData.some((realEvent) => realEvent.key === post.key) &&
            !(post.scheduleContentId && realScheduleContentIds.has(post.scheduleContentId)),
        ),
      ].sort((a, b) => (tempPostsEvents.length > 0 ? dayjs(a.start).diff(dayjs(b.start)) : 0)),
      (event) => (!!event.key ? event.key : event.scheduleContentId),
    )
  }, [eventsData, tempPostsEvents])

  // Sync the file preview image for the posts that are being generated, trigger the sync when the urls array changes
  useEffect(() => {
    if (calendarV2) return
    tempPosts.forEach((post) => {
      if (
        post.almanac_file?.key &&
        eventsData.some(
          (event) =>
            event.key === post.almanac_file?.key &&
            ((post.almanac_file?.image_url && !event.image_url) ||
              (post.almanac_file?.design_content && !event.design_content)),
        )
      ) {
        // Map almanac_file from AlmanacFileCalendarItem to AlmanacFileIndexItem shape for sync
        const almanacFile = post.almanac_file
        if (almanacFile) {
          const mappedFile: AlmanacFileIndexItem = {
            id: almanacFile.key,
            key: almanacFile.key,
            title: almanacFile.title ?? '',
            post_at: almanacFile.post_at || null,
            created_at: almanacFile.created_at,
            updated_at: almanacFile.updated_at || null,
            viewed_at: almanacFile.viewed_at || null,
            post_status: (almanacFile.status as PostStatusPropertyValue) || null,
            asset_type: almanacFile.asset_type || null,
            channel: almanacFile.channel || null,
            image_url: almanacFile.image_url || null,
            duration: almanacFile.duration ?? undefined,
            post_url: almanacFile.post_url || null,
            image_url_generating: almanacFile.image_url_generating || false,
            scheduled_post_targets: almanacFile.scheduled_post_targets || [],
            scheduled_post_id: almanacFile.scheduled_post_id ?? null,
            emoji: almanacFile.emoji || null,
            channel_emoji: almanacFile.channel_emoji || null,
            parent_id: almanacFile.parent_id || null,
            external_integration_id: almanacFile.external_integration_id || null,
            design_content: almanacFile.design_content || null,
            content: almanacFile.content || null,
            width: almanacFile.width || undefined,
            height: almanacFile.height || undefined,
            view_metadata: null,
            did_generation_fail: almanacFile.did_generation_fail || false,
            is_generating: almanacFile.is_generating || false,
            reviewed_at: almanacFile.reviewed_at || null,
            is_design_blank: almanacFile.is_design_blank || false,
            is_ai_generated_video: almanacFile.is_ai_generated_video || false,
            seo_string: null,
            is_carousel: almanacFile.is_carousel,
            content_type: almanacFile.content_type || null,
            progress: almanacFile.progress || null,
            schedule_content_id: almanacFile.schedule_content_id || null,
            reference_image_url: almanacFile.reference_image_url || null,
          }
          syncFilePreviewImage(mappedFile)
        }
      }
    })
  }, [calendarV2, tempPosts, eventsData, syncFilePreviewImage])

  if (calendarV2) {
    const someLoading = eventsData.some((e) => e.is_loading)
    return {
      allEvents: eventsData,
      allGeneratingContentsHaveLoadedPreview: !someLoading,
      eventsByDay: groupBy(
        eventsData.filter((event) => event.start && dayjs(event.start).isValid()),
        (event) => dayjs(event.start).startOf('day').format('YYYY-MM-DD'),
      ),
      eventsData,
      filters,
      hasInitialData,
      isGenerating: someLoading,
      isLoading,
      tempPosts: [],
    }
  }

  const eventsByDay = groupBy(
    (events as AlmanacFileCalendarEvent[]).filter((event) => event.start && dayjs(event.start).isValid()),
    (event) => dayjs(event.start).startOf('day').format('YYYY-MM-DD'),
  )

  return {
    allEvents: events as AlmanacFileCalendarEvent[],
    allGeneratingContentsHaveLoadedPreview,
    eventsByDay,
    eventsData,
    filters,
    hasInitialData,
    isGenerating,
    isLoading,
    tempPosts,
  }
}
